import {
  BOM,
  HEADER_FIXED,
  ROW_KIND_ACTUAL,
  ROW_KIND_BUDGET,
  TEMPLATE_COMMENT_LINES,
} from "./constants";
import { joinCsvRow } from "./csvLowLevel";
import { getMonthHeaderLabels, getOrderedMonthKeys } from "./monthOrder";
import type {
  BudgetCsvOptions,
  CategoryGroup,
  MonthKey,
  RowKind,
  SerializableBudgetEntry,
} from "./types";

const formatMonthCell = (v: number | null | undefined): string =>
  v === null || v === undefined ? "" : String(v);

function buildHeaderRow(startMonth: number): string {
  return joinCsvRow([
    ...HEADER_FIXED,
    ...getMonthHeaderLabels(startMonth),
  ]);
}

function buildDataRow(args: {
  fiscalYear: string;
  group: CategoryGroup;
  categoryGroup: string;
  category: string;
  rowKind: RowKind;
  months: Record<MonthKey, number | null>;
  monthOrder: MonthKey[];
}): string {
  const monthCells = args.monthOrder.map((k) => formatMonthCell(args.months[k]));
  return joinCsvRow([
    args.fiscalYear,
    args.group,
    args.categoryGroup,
    args.category,
    args.rowKind,
    ...monthCells,
  ]);
}

const hasAnyValue = (months: Record<MonthKey, number | null>): boolean =>
  Object.values(months).some((v) => v !== null && v !== undefined);

/**
 * 既存予実データを CSV 文字列に変換 (UTF-8 BOM 付き)。
 * 出力ルール (CSV v3.2):
 *   - 予算: 明細項目(科目)ごと 1 行 (全月併記)
 *   - 実績: 明細項目(科目)ごと 1 行 (科目単位の合計、全月併記)。詳細(区画)内訳は廃止。
 */
export function serializeBudgetCsv(
  entries: SerializableBudgetEntry[],
  options: BudgetCsvOptions,
): string {
  const monthOrder = getOrderedMonthKeys(options.startMonth);
  const lines: string[] = [];
  lines.push(...TEMPLATE_COMMENT_LINES);
  lines.push(buildHeaderRow(options.startMonth));

  for (const entry of entries) {
    // 予算行: 明細項目ごと 1 行。
    lines.push(
      buildDataRow({
        fiscalYear: entry.fiscalYear,
        group: entry.group,
        categoryGroup: entry.categoryGroup,
        category: entry.category,
        rowKind: ROW_KIND_BUDGET,
        months: entry.budgetMonths,
        monthOrder,
      }),
    );

    // 実績行: 明細項目(科目)単位の合計を 1 行 (値があるときのみ)。
    if (hasAnyValue(entry.actualMonths)) {
      lines.push(
        buildDataRow({
          fiscalYear: entry.fiscalYear,
          group: entry.group,
          categoryGroup: entry.categoryGroup,
          category: entry.category,
          rowKind: ROW_KIND_ACTUAL,
          months: entry.actualMonths,
          monthOrder,
        }),
      );
    }
  }

  return BOM + lines.join("\r\n") + "\r\n";
}

/**
 * ヘッダー + コメントのみの空テンプレート。データ行は 0。
 */
export function serializeEmptyTemplate(options: BudgetCsvOptions): string {
  const lines = [
    ...TEMPLATE_COMMENT_LINES,
    buildHeaderRow(options.startMonth),
  ];
  return BOM + lines.join("\r\n") + "\r\n";
}
